<template>
  <div class="flex flex-col p-16 gap-12 border-b border-neutral-border">
    <IntegrationWorkflowWrapper
      v-model:is-enabled="trayState.isEnabled"
      title="Enable Data Push"
      :description="`Push partner overlap data into ${trayState.friendlyName}, making it available for your revenue team in ${trayState.friendlyName} workflows, reports, dashboards and more.`"
      class="p-0"
    />
    <IndividualWorkflowStatus
      v-for="status in statuses"
      :key="status?.scheduled_workflow_name"
      :status-name="status.statusName"
      :callout-text="status.calloutText"
      :status="status.status"
      :last-pushed="status.last_completed_timestamp"
      :last-pushed-in-tooltip="true"
    />
    <div v-if="version">
      <span class="c-header-title">Version</span>
      <BittsBadge class="c-header-badge no-icon" :text="`v${version}`" />
    </div>
    <div v-if="createdAt">
      <span class="c-header-title">Last authorized</span>
      <span class="text-neutral-text-strong">{{ createdAt }}</span>
    </div>
    <div v-if="authBy" class="flex items-center justify-between">
      <span class="text-neutral-text-weak">Authorized by</span>
      <span class="text-neutral-text-strong">{{ authBy }}</span>
    </div>
    <div class="flex justify-between">
      <BittsButton
        v-if="!hideReauth"
        v-bind="buttonOptions"
        data-testid="integration-reauth-button"
        text="Re-authorize"
        :left-icon="['fak', 'cycle']"
        @click="() => emit('reauthorize')"
      />
      <BittsButton
        v-if="!hideReauth && !hideAdvancedSettings"
        v-bind="buttonOptions"
        data-testid="integration-reauth-button"
        text="Advanced settings"
        :right-icon="['fak', 'external-link']"
        @click="() => emit('reauthorize')"
        variant="ghost"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsBadge, BittsButton, BittsButtonProps } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed, inject, toRef } from 'vue';

import useTrayStatus from '@/composables/useTrayStatus';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { usePartnerCloudStore } from '@/stores';
import {
  TrayError,
  TrayStatus,
  TrayWorkflowName,
  WorkflowsWithStatus,
} from '@/types/integrations';

import IndividualWorkflowStatus from './IndividualWorkflowStatus.vue';
import IntegrationWorkflowWrapper from './IntegrationWorkflowWrapper.vue';

const props = defineProps<{
  version?: string | null;
  created?: string | null;
  authBy?: string | null;
  loading?: boolean;
  disabled?: boolean;
  hideReauth?: boolean;
  hideAdvancedSettings?: boolean;
}>();
const emit = defineEmits<{
  reauthorize: [];
}>();

const partnerCloudStore = usePartnerCloudStore();
const { getStatusByWorkflowName } = partnerCloudStore;

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;
const { trayStatuses, higherOrderError, errorInfo, getCalloutText } =
  useTrayStatus(toRef(trayState.enabledTray));

const isWorkflowPaused = (workflowName: WorkflowsWithStatus) => {
  switch (workflowName) {
    case 'SCH.Ecosystem fields to CSV [SFDC Ecosystem]':
    case 'Sch. HubSpot Ecosystem Fields Push':
      return trayState.accountPush === 'NONE';

    case 'SCH.Partner fields to CSV [SFDC Partner]':
    case 'Sch. HubSpot Partner Fields Push':
      return !trayState.partnerFieldMappingsPush;

    case 'Sch. Entry Point: HubSpot Overlap Push':
    case 'SCH.Overlaps to Salesforce [SFDC Overlaps]':
      return !trayState.customObjectPush;

    default:
      return false;
  }
};

const getStatus = (status: TrayStatus) => {
  if (higherOrderError.value) return errorInfo.value.calloutType;
  if (isWorkflowPaused(status.scheduled_workflow_name)) return 'warning';
  if (status?.error?.error_type) return 'error';
  return 'success';
};

type IndividualStatus = TrayStatus & {
  status: 'success' | 'warning' | 'error' | 'pending';
  calloutText: string;
  statusName: string;
};

const buildCalloutText = (status: TrayStatus) => {
  if (higherOrderError.value) return errorInfo.value.calloutText;
  if (isWorkflowPaused(status.scheduled_workflow_name)) return '';
  return getCalloutText(status?.error?.error_type as TrayError);
};

const workflowNameToStatusName: Record<TrayWorkflowName, string> = {
  'SCH.Partner fields to CSV [SFDC Partner]': 'Partner Fields Push Status',
  'Sch. HubSpot Partner Fields Push': 'Partner Fields Push Status',
  'SCH.Ecosystem fields to CSV [SFDC Ecosystem]': 'Account Object Push Status',
  'Sch. HubSpot Ecosystem Fields Push': 'Company Object Push Status',
  'Sch. Entry Point: HubSpot Overlap Push': 'Custom Object Push Status',
  'SCH.Overlaps to Salesforce [SFDC Overlaps]': 'Custom Object Push Status',
  'Sch. Create Hubspot Lists [CB HS]': 'HubSpot Lists Push Status',
  'Sch. Crossbeam Operations [CB Marketo]': 'Status',
  'SCH. Crossbeam to Dynamics [CB DYN]': 'Status',
  'SCH.CB Attributions to SFDC [CD SFDC]': 'Status',
  'Sch. Get and Format Records [Gong]': 'Status',
};

const statuses = computed(() =>
  trayStatuses.value
    .reduce<IndividualStatus[]>((acc, ts) => {
      const status = getStatusByWorkflowName(ts.scheduled_workflow_name);
      if (!status) return acc;
      acc.push({
        ...status,
        status: getStatus(status),
        calloutText: buildCalloutText(status),
        statusName:
          workflowNameToStatusName[ts.scheduled_workflow_name] || 'Status',
        last_completed_timestamp: ts.last_completed_timestamp,
      });
      return acc;
    }, [])
    .sort((a, b) => a.statusName.localeCompare(b.statusName)),
);

const formatDateTime = (isoString?: string) =>
  isoString
    ? DateTime.fromISO(isoString).toFormat("LLL dd, yyyy 'at' h:mm a")
    : undefined;

const createdAt = computed(() => formatDateTime(props.created || ''));

const buttonOptions = computed<BittsButtonProps>(() => ({
  loading: props.loading,
  disabled: props.disabled,
  variant: 'outline',
  type: 'neutral',
  size: 'small',
}));
</script>
